<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 API Dropdown Component
      p.text--secondary An API-driven dropdown component built on YzDropdown that supports dynamic data loading, pagination, and customizable loader states for server-side data integration.

    // Basic API Usage
    .component-section.mb-8#basic-api-usage
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-api-usage')") Basic API Usage
      .variation-item.mb-6#basic-api-usage-patient
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('basic-api-usage-patient')") Patient Dropdown with Search
          p.text--secondary.caption Fetches patient data from API with integrated search and pagination

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(basicApiValue) }}
          .mb-3
            strong Loading: {{ isBasicLoading }}
          .mb-3
            strong Current Page: {{ basicCurrentPage }}
            span.ml-2.text--secondary / {{ Math.ceil(basicTotalCount / basicPageSize) }} pages
          yz-api-dropdown(
            v-model="basicApiValue"
            :items="basicApiItems"
            :loading="isBasicLoading"
            :count="basicTotalCount"
            :page-size="basicPageSize"
            :page.sync="basicCurrentPage"
            label="Select Patient"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            @opened="fetchPatients"
            @searched="onSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === basicApiCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === basicApiCode ? 'success' : ''" @click="copyCode(basicApiCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ basicApiCode }}

    // Custom Item Template
    .component-section.mb-8#custom-item-template
      h3.mb-4.cursor-pointer(@click="navigateToSection('custom-item-template')") Custom Item Template
      .variation-item.mb-6#custom-item-template-single
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('custom-item-template-single')") Rich Patient Display
          p.text--secondary.caption Custom item slot with additional information

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(customTemplateValue) }}
          yz-api-dropdown(
            v-model="customTemplateValue"
            :items="customTemplateItems"
            :loading="isCustomTemplateLoading"
            :count="customTemplateTotalCount"
            :page-size="customTemplatePageSize"
            :page.sync="customTemplateCurrentPage"
            label="Select Patient (Custom Template)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            @opened="fetchPatientsCustomTemplate"
            @searched="onCustomTemplateSearch"
          )
            template(#item="{ item }")
              .d-flex.justify-space-between.align-center
                div
                  span {{ item.name }}
                  .yz-regular-body-xs.text-neutral-500 {{ item.ic }}
                yz-btn(icon-only icon="mdi-trash-can" icon-color="danger" @click.stop="onClickCustomTemplateDelete(item)")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === customTemplateCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === customTemplateCode ? 'success' : ''" @click="copyCode(customTemplateCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ customTemplateCode }}

      // Multiple Rich Patient Display
      .variation-item.mb-6#custom-item-template-multiple
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('custom-item-template-multiple')") Rich Patient Display (Multiple)
          p.text--secondary.caption Custom item slot with additional information for multiple selection

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(customTemplateMultipleValue) }}
          yz-api-dropdown(
            v-model="customTemplateMultipleValue"
            :items="customTemplateMultipleItems"
            :loading="isCustomTemplateMultipleLoading"
            :count="customTemplateMultipleTotalCount"
            :page-size="customTemplateMultiplePageSize"
            :page.sync="customTemplateMultipleCurrentPage"
            label="Select Multiple Patients (Custom Template)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            multiple
            @opened="fetchPatientsCustomTemplateMultiple"
            @searched="onCustomTemplateMultipleSearch"
          )
            template(#item="{ item }")
              .d-flex.justify-space-between.align-center
                div
                  span {{ item.name }}
                  .yz-regular-body-xs.text-neutral-500 {{ item.ic }}
                yz-btn(icon-only icon="mdi-trash-can" icon-color="danger" @click.stop="onClickCustomTemplateMultipleDelete(item)")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === customTemplateMultipleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === customTemplateMultipleCode ? 'success' : ''" @click="copyCode(customTemplateMultipleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ customTemplateMultipleCode }}


    // Multiple Selection with API
    .component-section.mb-8#multiple-api
      h3.mb-4.cursor-pointer(@click="navigateToSection('multiple-api')") Multiple Selection with API

      // Multiple without chips - Returns Objects
      .variation-item.mb-6#multiple-no-chips-objects
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('multiple-no-chips-objects')") Multi-select without Chips
          p.text--secondary.caption Multiple selection with list display, returns objects

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(multipleNoChipsValue) }}
          yz-api-dropdown(
            v-model="multipleNoChipsValue"
            :items="multipleNoChipsItems"
            :loading="isMultipleNoChipsLoading"
            :count="multipleNoChipsTotalCount"
            :page-size="multipleNoChipsPageSize"
            :page.sync="multipleNoChipsCurrentPage"
            label="Select Multiple Patients"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            multiple
            @opened="fetchMultipleNoChips"
            @searched="onMultipleNoChipsSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === multipleNoChipsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === multipleNoChipsCode ? 'success' : ''" @click="copyCode(multipleNoChipsCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ multipleNoChipsCode }}

      // Multiple with chips - Returns Objects  
      .variation-item.mb-6#multiple-with-chips-objects
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('multiple-with-chips-objects')") Multi-select with Chips
          p.text--secondary.caption Multiple selection with chip display for compact overview, returns objects

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(multipleWithChipsReturnObjectValue) }}
          yz-api-dropdown(
            v-model="multipleWithChipsReturnObjectValue"
            :items="multipleWithChipsReturnObjectItems"
            :loading="isMultipleWithChipsReturnObjectLoading"
            :count="multipleWithChipsReturnObjectTotalCount"
            :page-size="multipleWithChipsReturnObjectPageSize"
            :page.sync="multipleWithChipsReturnObjectCurrentPage"
            label="Select Multiple Patients"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            multiple
            chips
            @opened="fetchMultipleWithChipsReturnObject"
            @searched="onMultipleWithChipsReturnObjectSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === multipleWithChipsReturnObjectCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === multipleWithChipsReturnObjectCode ? 'success' : ''" @click="copyCode(multipleWithChipsReturnObjectCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ multipleWithChipsReturnObjectCode }}

      // Multiple with custom template and chips
      .variation-item.mb-6#multiple-custom-template-chips
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('multiple-custom-template-chips')") Multi-select with Custom Template and Chips
          p.text--secondary.caption Multiple selection with custom item display and chip overview

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(multipleCustomValue) }}
          yz-api-dropdown(
            v-model="multipleCustomValue"
            :items="multipleCustomItems"
            :loading="isMultipleCustomLoading"
            :count="multipleCustomTotalCount"
            :page-size="multipleCustomPageSize"
            :page.sync="multipleCustomCurrentPage"
            label="Select Multiple Patients"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            multiple
            chips
            @opened="fetchMultipleCustom"
            @searched="onMultipleCustomSearch"
          )
            template(#item="{ item }")
              .d-flex.align-center
                .mr-3
                  v-avatar(size="32")
                    yz-img(v-if="item.profile_image" :src="item.profile_image.file")
                    yz-icon(v-else) mdi-account
                .d-flex.flex-column
                  .font-weight-medium {{ item.name }}
                  .text-caption.text--secondary {{ item.role || 'Staff' }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === multipleCustomCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === multipleCustomCode ? 'success' : ''" @click="copyCode(multipleCustomCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ multipleCustomCode }}

    // Clearable Features
    .component-section.mb-8#clearable-features
      h3.mb-4.cursor-pointer(@click="navigateToSection('clearable-features')") Clearable Features

      .variation-item.mb-6#clearable-single
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('clearable-single')") Clearable (Single Selection)
          p.text--secondary.caption Single selection with clear functionality. Clear icon appears when there is a value.

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(clearableSingleValue) }}
          yz-api-dropdown(
            v-model="clearableSingleValue"
            :items="clearableSingleItems"
            :loading="isClearableSingleLoading"
            :count="clearableSingleTotalCount"
            :page-size="clearableSinglePageSize"
            :page.sync="clearableSingleCurrentPage"
            label="Select Framework (Clearable)"
            placeholder="Choose a framework..."
            item-text="name"
            item-value="id"
            clearable
            @opened="fetchClearableSingle"
            @searched="onClearableSingleSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === clearableSingleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === clearableSingleCode ? 'success' : ''" @click="copyCode(clearableSingleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ clearableSingleCode }}

      .variation-item.mb-6#clearable-multiple
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('clearable-multiple')") Clearable (Multiple Selection)
          p.text--secondary.caption Multiple selections with clear functionality. Clear icon appears to clear all selections.

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(clearableMultipleValue) }}
          yz-api-dropdown(
            v-model="clearableMultipleValue"
            :items="clearableMultipleItems"
            :loading="isClearableMultipleLoading"
            :count="clearableMultipleTotalCount"
            :page-size="clearableMultiplePageSize"
            :page.sync="clearableMultipleCurrentPage"
            label="Select Languages (Clearable)"
            placeholder="Choose languages..."
            item-text="name"
            item-value="id"
            multiple
            clearable
            @opened="fetchClearableMultiple"
            @searched="onClearableMultipleSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === clearableMultipleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === clearableMultipleCode ? 'success' : ''" @click="copyCode(clearableMultipleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ clearableMultipleCode }}

      .variation-item.mb-6#clearable-chips-single
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('clearable-chips-single')") Clearable Chips (Single Selection)
          p.text--secondary.caption Single selection with chips and clear functionality. Clear icon appears when there is a value.

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(clearableChipsSingleValue) }}
          yz-api-dropdown(
            v-model="clearableChipsSingleValue"
            :items="clearableChipsSingleItems"
            :loading="isClearableChipsSingleLoading"
            :count="clearableChipsSingleTotalCount"
            :page-size="clearableChipsSinglePageSize"
            :page.sync="clearableChipsSingleCurrentPage"
            label="Select Framework (Clearable Chips)"
            placeholder="Choose a framework..."
            item-text="name"
            item-value="id"
            chips
            clearable
            @opened="fetchClearableChipsSingle"
            @searched="onClearableChipsSingleSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === clearableChipsSingleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === clearableChipsSingleCode ? 'success' : ''" @click="copyCode(clearableChipsSingleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ clearableChipsSingleCode }}

      .variation-item.mb-6#clearable-chips-multiple
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('clearable-chips-multiple')") Clearable Chips (Multiple Selection)
          p.text--secondary.caption Multiple selections with chips and clear functionality. Individual chips can be closed, plus clear icon for all.

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(clearableChipsMultipleValue) }}
          yz-api-dropdown(
            v-model="clearableChipsMultipleValue"
            :items="clearableChipsMultipleItems"
            :loading="isClearableChipsMultipleLoading"
            :count="clearableChipsMultipleTotalCount"
            :page-size="clearableChipsMultiplePageSize"
            :page.sync="clearableChipsMultipleCurrentPage"
            label="Select Languages (Clearable Chips)"
            placeholder="Choose languages..."
            item-text="name"
            item-value="id"
            multiple
            chips
            clearable
            @opened="fetchClearableChipsMultiple"
            @searched="onClearableChipsMultipleSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === clearableChipsMultipleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === clearableChipsMultipleCode ? 'success' : ''" @click="copyCode(clearableChipsMultipleCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ clearableChipsMultipleCode }}

    // Empty State Slots
    .component-section.mb-8#empty-state-slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('empty-state-slots')") Empty State Slots
      .variation-item.mb-6#empty-state-no-data
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('empty-state-no-data')") No Data State
          p.text--secondary.caption Custom content when no items are provided

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          yz-api-dropdown(
            v-model="emptyStateValue"
            :items="emptyStateItems"
            :loading="false"
            label="Select Patient"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            no-data-text="Custom no data message"
            searchable
          )
            template(#no-data)
              .pa-6.text-center
                yz-icon.mb-2(size="48" color="neutral-400") mdi-account-off
                .yz-regular-body-md.text-neutral-600.mb-2 No patients available
                .text-caption.text-neutral-500 Try adding some patients first
                yz-btn.mt-3(size="sm" outlined label="Add patient")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === emptyStateCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === emptyStateCode ? 'success' : ''" @click="copyCode(emptyStateCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ emptyStateCode }}

      .variation-item.mb-6#empty-state-no-results
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('empty-state-no-results')") No Results State
          p.text--secondary.caption Custom content when search yields no results

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Search for "xyz" to see no results state
          yz-api-dropdown(
            v-model="noResultsValue"
            :items="noResultsItems"
            :loading="false"
            label="Search Patients"
            placeholder="Type to search..."
            item-text="name"
            item-value="id"
            no-results-text="Custom no results message"
            searchable
            @searched="onNoResultsSearch"
          )
            template(#no-results)
              .pa-6.text-center
                yz-icon.mb-2(size="48" color="neutral-400") mdi-magnify-close
                .yz-regular-body-md.text-neutral-600.mb-2 No matching patients found
                .text-caption.text-neutral-500 Try adjusting your search criteria
                yz-btn.mt-3(size="sm" outlined label="Clear Search")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === noResultsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === noResultsCode ? 'success' : ''" @click="copyCode(noResultsCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ noResultsCode }}

    // Error State Handling
    .component-section.mb-8#error-state-handling
      h3.mb-4.cursor-pointer(@click="navigateToSection('error-state-handling')") Error State Handling
      .variation-item.mb-6#error-state-default
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('error-state-default')") Default Error State
          p.text--secondary.caption Show error state when API calls fail

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Error State: {{ errorStateError }}
          .mb-3
            yz-btn(size="sm" outlined label="Simulate API Error" @click="simulateApiError")
            yz-btn.ml-2(size="sm" label="Reset" @click="resetErrorState")
          yz-api-dropdown(
            v-model="errorStateValue"
            :items="errorStateItems"
            :loading="isErrorStateLoading"
            :error="errorStateError"
            :count="errorStateTotalCount"
            :page-size="errorStatePageSize"
            :page.sync="errorStateCurrentPage"
            label="Select Patient (Error Example)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            @opened="fetchErrorStatePatients"
            @searched="onErrorStateSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === errorStateCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === errorStateCode ? 'success' : ''" @click="copyCode(errorStateCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ errorStateCode }}

      .variation-item.mb-6#error-state-custom-message
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('error-state-custom-message')") Custom Error Message
          p.text--secondary.caption Use custom error message via prop

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Error State: {{ customErrorStateError }}
          .mb-3
            yz-btn(size="sm" outlined label="Simulate Network Error" @click="simulateCustomError")
            yz-btn.ml-2(size="sm" label="Reset" @click="resetCustomError")
          yz-api-dropdown(
            v-model="customErrorStateValue"
            :items="customErrorStateItems"
            :loading="isCustomErrorStateLoading"
            :error="customErrorStateError"
            error-message="Network connection failed. Please check your internet connection and try again."
            :count="customErrorStateTotalCount"
            :page-size="customErrorStatePageSize"
            :page.sync="customErrorStateCurrentPage"
            label="Select Patient (Custom Error)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            @opened="fetchCustomErrorStatePatients"
            @searched="onCustomErrorStateSearch"
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === customErrorCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === customErrorCode ? 'success' : ''" @click="copyCode(customErrorCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ customErrorCode }}

      .variation-item.mb-6#error-state-custom-slot
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('error-state-custom-slot')") Custom Error Slot
          p.text--secondary.caption Fully customizable error content with actions

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Error State: {{ slotErrorStateError }}
          .mb-3
            yz-btn(size="sm" outlined label="Simulate Server Error" @click="simulateSlotError")
            yz-btn.ml-2(size="sm" label="Reset" @click="resetSlotError") Reset
          yz-api-dropdown(
            v-model="slotErrorStateValue"
            :items="slotErrorStateItems"
            :loading="isSlotErrorStateLoading"
            :error="slotErrorStateError"
            :count="slotErrorStateTotalCount"
            :page-size="slotErrorStatePageSize"
            :page.sync="slotErrorStateCurrentPage"
            label="Select Patient (Custom Error Slot)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            @opened="fetchSlotErrorStatePatients"
            @searched="onSlotErrorStateSearch"
          )
            template(#error)
              .pa-6.text-center
                yz-icon.mb-3(size="48" color="danger") mdi-alert-circle
                .yz-regular-body-md.text-danger-600.mb-2 Server Error Occurred
                .text-caption.text-neutral-600.mb-4 Unable to load patient data. Our servers are experiencing issues.
                .d-flex.justify-center.gap-2
                  yz-btn(size="sm" outlined label="Try Again" @click="retrySlotError")
                  yz-btn(size="sm" label="Contact Support" @click="contactSupport")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === customSlotErrorCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === customSlotErrorCode ? 'success' : ''" @click="copyCode(customSlotErrorCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ customSlotErrorCode }}

    // Header and Footer Slots
    .component-section.mb-8#header-footer-slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('header-footer-slots')") Header and Footer Slots
      .variation-item.mb-6#header-slot-example
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('header-slot-example')") Header Slot
          p.text--secondary.caption Custom content shown above the item list

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(headerValue) }}
          .mb-3
            strong Total Patients: {{ headerTotalCount }}
          yz-api-dropdown(
            v-model="headerValue"
            :items="headerItems"
            :loading="isHeaderLoading"
            :count="headerTotalCount"
            :page-size="headerPageSize"
            :page.sync="headerCurrentPage"
            label="Select Patient (Header Example)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            searchable
            @opened="fetchHeaderPatients"
            @searched="onHeaderSearch"
          )
            template(#header)
              .pa-3.border-bottom.bg-primary-50
                .d-flex.justify-space-between.align-center
                  .d-flex.align-center
                    yz-icon.mr-2(size="sm" color="primary") mdi-account-group
                    span.yz-regular-body-sm.text-primary-700 {{ headerTotalCount }} patients available
                    span.ml-2.text-caption.text-neutral-500(v-if="headerSearchQuery") (filtered by "{{ headerSearchQuery }}")
                  yz-btn(size="sm" icon="mdi-plus" variant="primary" @click="addNewPatient") Add Patient

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === headerSlotCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === headerSlotCode ? 'success' : ''" @click="copyCode(headerSlotCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ headerSlotCode }}

      .variation-item.mb-6#footer-slot-example
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('footer-slot-example')") Footer Slot
          p.text--secondary.caption Custom content shown below the item list

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(footerValue) }}
          .mb-3
            strong Current Page: {{ footerCurrentPage }} / {{ Math.ceil(footerTotalCount / footerPageSize) }}
          yz-api-dropdown(
            v-model="footerValue"
            :items="footerItems"
            :loading="isFooterLoading"
            :count="footerTotalCount"
            :page-size="footerPageSize"
            :page.sync="footerCurrentPage"
            label="Select Patient (Footer Example)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            searchable
            @opened="fetchFooterPatients"
            @searched="onFooterSearch"
          )
            template(#footer)
              .pa-3.border-top.bg-neutral-50
                .text-center
                  .text-caption.text-neutral-600.mb-2
                    | Showing {{ footerItems.length }} of {{ footerTotalCount }} patients
                    span(v-if="footerValue") • 1 selected
                  .d-flex.justify-center.gap-2
                    yz-btn(size="sm" variant="text" icon="mdi-eye") View All
                    yz-btn(size="sm" variant="text" icon="mdi-download") Export List

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === footerSlotCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === footerSlotCode ? 'success' : ''" @click="copyCode(footerSlotCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ footerSlotCode }}

      .variation-item.mb-6#header-footer-combined
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('header-footer-combined')") Combined Header and Footer
          p.text--secondary.caption Both header and footer slots working together

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(combinedValue) }}
          .mb-3
            strong Stats: {{ combinedItems.length }} items on page {{ combinedCurrentPage }}
          yz-api-dropdown(
            v-model="combinedValue"
            :items="combinedItems"
            :loading="isCombinedLoading"
            :count="combinedTotalCount"
            :page-size="combinedPageSize"
            :page.sync="combinedCurrentPage"
            label="Select Patient (Combined Example)"
            placeholder="Choose patients..."
            item-text="name"
            item-value="id"
            searchable
            @opened="fetchCombinedPatients"
            @searched="onCombinedSearch"
          )
            template(#header)
              .pa-3.border-bottom.bg-primary-50
                .d-flex.justify-space-between.align-center
                  .d-flex.align-center
                    yz-icon.mr-2(size="sm" color="primary") mdi-account-group
                    span.yz-regular-body-sm.text-primary-700 {{ combinedTotalCount }} patients available
                    span.ml-2.text-caption.text-neutral-500(v-if="combinedSearchQuery") (filtered by "{{ combinedSearchQuery }}")
                  yz-btn(size="sm" icon="mdi-plus" variant="primary" @click="addNewPatient") Add Patient
            template(#footer)
              .pa-3.border-top.bg-neutral-50
                .text-center
                  .text-caption.text-neutral-600.mb-2
                    | Showing {{ combinedItems.length }} of {{ combinedTotalCount }} patients
                    span(v-if="combinedValue") • 1 selected
                  .d-flex.justify-center.gap-2
                    yz-btn(size="sm" variant="text" icon="mdi-eye") View All
                    yz-btn(size="sm" variant="text" icon="mdi-download") Export List

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === combinedSlotCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === combinedSlotCode ? 'success' : ''" @click="copyCode(combinedSlotCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ combinedSlotCode }}

    // v-form Validation Examples
    .component-section.mb-8#validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('validation-examples')") v-form Validation
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('validation-examples')") v-form Integration
          p.text--secondary.caption Modern validation with v-form integration, custom rules, and reactive validation with API data

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          // Basic v-form example
          .mb-4
            h5.mb-2 Basic v-form with Required Validation
            .mb-2
              small.text--secondary Form Valid: {{ formValid ? 'Yes' : 'No' }} | Selected: {{ JSON.stringify(basicFormValue) }}
            v-form(ref="basicForm" v-model="formValid")
              yz-api-dropdown(
                v-model="basicFormValue"
                :items="basicFormItems"
                :loading="isBasicFormLoading"
                :count="basicFormTotalCount"
                :page-size="basicFormPageSize"
                :page.sync="basicFormCurrentPage"
                label="Select Patient"
                placeholder="Choose a patient..."
                item-text="name"
                item-value="id"
                required
                required-error-message="Please select a patient"
                clearable
                @opened="fetchBasicFormPatients"
                @searched="onBasicFormSearch"
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetBasicForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateBasicForm")
              .mt-2(v-if="basicFormValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': basicFormValidationResult === 'Valid', 'error--text': basicFormValidationResult === 'Invalid' }") {{ basicFormValidationResult }}

          v-divider.my-4

          // Custom rules example
          .mb-4
            h5.mb-2 Custom Validation Rules with API
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(customRulesValue) }}
            v-form(ref="customRulesForm" v-model="customRulesFormValid")
              yz-api-dropdown(
                v-model="customRulesValue"
                :items="customRulesItems"
                :loading="isCustomRulesLoading"
                :count="customRulesTotalCount"
                :page-size="customRulesPageSize"
                :page.sync="customRulesCurrentPage"
                required
                :rules="customApiDropdownRules"
                label="Custom Rules Dropdown"
                placeholder="Cannot select first patient..."
                item-text="name"
                item-value="id"
                clearable
                @opened="fetchCustomRulesPatients"
                @searched="onCustomRulesSearch"
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetCustomRulesForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateCustomRulesForm")
              .mt-2(v-if="customRulesValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': customRulesValidationResult === 'Valid', 'error--text': customRulesValidationResult === 'Invalid' }") {{ customRulesValidationResult }}

          v-divider.my-4

          // Multiple selection with rules
          .mb-4
            h5.mb-2 Multiple Selection with Custom Rules
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(multipleRulesValue) }}
            v-form(ref="multipleRulesForm" v-model="multipleRulesFormValid")
              yz-api-dropdown(
                v-model="multipleRulesValue"
                :items="multipleRulesItems"
                :loading="isMultipleRulesLoading"
                :count="multipleRulesTotalCount"
                :page-size="multipleRulesPageSize"
                :page.sync="multipleRulesCurrentPage"
                :rules="multipleApiDropdownRules"
                label="Multiple with Rules"
                placeholder="Select 2-3 patients..."
                item-text="name"
                item-value="id"
                multiple
                @opened="fetchMultipleRulesPatients"
                @searched="onMultipleRulesSearch"
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetMultipleRulesForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateMultipleRulesForm")
              .mt-2(v-if="multipleRulesValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': multipleRulesValidationResult === 'Valid', 'error--text': multipleRulesValidationResult === 'Invalid' }") {{ multipleRulesValidationResult }}

          v-divider.my-4

          // Multiple selection with chips validation
          .mb-4
            h5.mb-2 Multiple Selection with Chips Validation
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(chipsMultipleRulesValue) }}
            v-form(ref="chipsMultipleRulesForm" v-model="chipsMultipleRulesFormValid")
              yz-api-dropdown(
                v-model="chipsMultipleRulesValue"
                :items="chipsRulesItems"
                :loading="isChipsRulesLoading"
                :count="chipsRulesTotalCount"
                :page-size="chipsRulesPageSize"
                :page.sync="chipsRulesCurrentPage"
                :rules="chipsApiRules"
                label="Select Patients"
                placeholder="Choose patients..."
                item-text="name"
                item-value="id"
                multiple
                chips
                clearable
                @opened="fetchChipsRulesPatients"
                @searched="onChipsRulesSearch"
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetChipsMultipleRulesForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateChipsMultipleRulesForm")
              .mt-2(v-if="chipsMultipleRulesValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': chipsMultipleRulesValidationResult === 'Valid', 'error--text': chipsMultipleRulesValidationResult === 'Invalid' }") {{ chipsMultipleRulesValidationResult }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === vformValidationCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === vformValidationCode ? 'success' : ''" @click="copyCode(vformValidationCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ vformValidationCode }}

    // Optional Validation Examples
    .component-section.mb-8#optional-validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Validation Examples
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional API Dropdown Selection
          p.text--secondary.caption Optional fields that validate only when values are selected - useful for API-driven filters, optional data sources, and dynamic preferences

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")

          // Optional department selection
          .mb-6
            h5.mb-2 Optional department filter (API-driven)
            small.text--secondary Selected: {{ JSON.stringify(optionalDepartmentValue) }}
            small.text--secondary.ml-2 | Loading: {{ isOptionalDepartmentLoading }}
            v-form.mt-2(ref="optionalDepartmentForm")
              yz-api-dropdown(
                v-model="optionalDepartmentValue"
                :items="optionalDepartmentItems"
                :loading="isOptionalDepartmentLoading"
                :count="optionalDepartmentCount"
                :page-size="10"
                :page.sync="optionalDepartmentPage"
                :rules="optionalDepartmentRules"
                label="Department (optional)"
                placeholder="Select department..."
                item-text="name"
                item-value="id"
                clearable
                @opened="fetchDepartments"
                @searched="onDepartmentSearch"
              )

            .d-flex.gap-2.mt-3
              yz-btn(label="Reset" size="sm" @click="resetOptionalDepartmentForm")
              yz-btn(label="Validate" @click="validateOptionalDepartmentForm" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="optionalDepartmentValidationResult" :class="{ 'success--text': optionalDepartmentValidationResult === 'Valid', 'error--text': optionalDepartmentValidationResult === 'Invalid' }") {{ optionalDepartmentValidationResult }}

          // Optional user selection
          .mb-6
            h5.mb-2 Optional user assignment (API-driven)
            small.text--secondary Selected: {{ JSON.stringify(optionalUserValue) }}
            small.text--secondary.ml-2 | Loading: {{ isOptionalUserLoading }}
            v-form.mt-2(ref="optionalUserForm")
              yz-api-dropdown(
                v-model="optionalUserValue"
                :items="optionalUserItems"
                :loading="isOptionalUserLoading"
                :count="optionalUserCount"
                :page-size="15"
                :page.sync="optionalUserPage"
                :rules="optionalUserRules"
                label="Assign User (optional)"
                placeholder="Choose user..."
                item-text="name"
                item-value="id"
                clearable
                @opened="fetchUsers"
                @searched="onUserSearch"
              )

            .d-flex.gap-2.mt-3
              yz-btn(label="Reset" size="sm" @click="resetOptionalUserForm")
              yz-btn(label="Validate" @click="validateOptionalUserForm" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="optionalUserValidationResult" :class="{ 'success--text': optionalUserValidationResult === 'Valid', 'error--text': optionalUserValidationResult === 'Invalid' }") {{ optionalUserValidationResult }}

          // Optional project selection
          .mb-6
            h5.mb-2 Optional project assignment (API-driven)
            small.text--secondary Selected: {{ JSON.stringify(optionalProjectValue) }}
            small.text--secondary.ml-2 | Loading: {{ isOptionalProjectLoading }}
            v-form.mt-2(ref="optionalProjectForm")
              yz-api-dropdown(
                v-model="optionalProjectValue"
                :items="optionalProjectItems"
                :loading="isOptionalProjectLoading"
                :count="optionalProjectCount"
                :page-size="20"
                :page.sync="optionalProjectPage"
                :rules="optionalProjectRules"
                label="Project (optional)"
                placeholder="Select project..."
                item-text="title"
                item-value="id"
                clearable
                @opened="fetchProjects"
                @searched="onProjectSearch"
              )

            .d-flex.gap-2.mt-3
              yz-btn(label="Reset" size="sm" @click="resetOptionalProjectForm")
              yz-btn(label="Validate" @click="validateOptionalProjectForm" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="optionalProjectValidationResult" :class="{ 'success--text': optionalProjectValidationResult === 'Valid', 'error--text': optionalProjectValidationResult === 'Invalid' }") {{ optionalProjectValidationResult }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            span.font-weight-medium Code Examples

          // Optional department selection code
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalDepartmentCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalDepartmentCode ? 'success' : ''" @click="copyCode(optionalDepartmentCode)")
              span.font-weight-medium.ml-2 Optional department selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalDepartmentCode }}

          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalUserCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalUserCode ? 'success' : ''" @click="copyCode(optionalUserCode)")
              span.font-weight-medium.ml-2 Optional user selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalUserCode }}

          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalProjectCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalProjectCode ? 'success' : ''" @click="copyCode(optionalProjectCode)")
              span.font-weight-medium.ml-2 Optional project selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalProjectCode }}

    // Readonly vs Disabled State Comparison
    .component-section.mb-8#readonly-disabled-comparison
      h3.mb-4.cursor-pointer(@click="navigateToSection('readonly-disabled-comparison')") Readonly vs Disabled State Comparison
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('readonly-disabled-comparison')") State Comparison with API Data
          p.text--secondary.caption Demonstrating the visual and functional differences between readonly and disabled states with API-driven dropdowns

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .d-flex.flex-column.gap-6
            // Single Selection Examples
            .mb-6
              h5.mb-3.text-primary Single Selection Examples (API-driven)
              .d-flex.flex-column.gap-4
                .mb-4
                  h6.mb-2 Normal (Interactive)
                  yz-api-dropdown(
                    v-model="basicApiValue"
                    :items="basicApiItems"
                    :loading="isBasicLoading"
                    :count="basicTotalCount"
                    :page-size="basicPageSize"
                    :page.sync="basicCurrentPage"
                    label="Normal Patient Dropdown"
                    placeholder="Can interact and select..."
                    item-text="name"
                    item-value="id"
                    clearable
                    @opened="fetchPatients"
                    @searched="onSearch"
                  )

                .mb-4
                  h6.mb-2 Readonly State
                  small.text--secondary.d-block.mb-2 Prevents interaction but maintains normal visual appearance. Value: {{ JSON.stringify(readonlyFormValue) }}
                  yz-api-dropdown(
                    v-model="readonlyFormValue"
                    :items="basicApiItems"
                    :loading="isBasicLoading"
                    :count="basicTotalCount"
                    :page-size="basicPageSize"
                    :page.sync="basicCurrentPage"
                    label="Readonly Patient Dropdown"
                    placeholder="Cannot interact..."
                    item-text="name"
                    item-value="id"
                    readonly
                    clearable
                    @opened="fetchPatients"
                    @searched="onSearch"
                  )

                .mb-4
                  h6.mb-2 Disabled State
                  small.text--secondary.d-block.mb-2 Prevents interaction with visual indicators (grayed out). Value: {{ JSON.stringify(disabledFormValue) }}
                  yz-api-dropdown(
                    v-model="disabledFormValue"
                    :items="basicApiItems"
                    :loading="isBasicLoading"
                    :count="basicTotalCount"
                    :page-size="basicPageSize"
                    :page.sync="basicCurrentPage"
                    label="Disabled Patient Dropdown"
                    placeholder="Cannot interact..."
                    item-text="name"
                    item-value="id"
                    disabled
                    clearable
                    @opened="fetchPatients"
                    @searched="onSearch"
                  )

            v-divider

            // Multiple Selection Examples (without chips)
            .mb-6
              h5.mb-3.text-primary Multiple Selection Examples (without chips)
              .d-flex.flex-column.gap-4
                .mb-4
                  h6.mb-2 Normal (Interactive)
                  small.text--secondary.d-block.mb-2 Value: {{ JSON.stringify(multipleNoChipsValue) }}
                  yz-api-dropdown(
                    v-model="multipleNoChipsValue"
                    :items="multipleNoChipsItems"
                    :loading="isMultipleNoChipsLoading"
                    :count="multipleNoChipsTotalCount"
                    :page-size="multipleNoChipsPageSize"
                    :page.sync="multipleNoChipsCurrentPage"
                    label="Normal Multiple Patient Dropdown"
                    placeholder="Can select multiple..."
                    item-text="name"
                    item-value="id"
                    multiple
                    clearable
                    @opened="fetchMultipleNoChips"
                    @searched="onMultipleNoChipsSearch"
                  )

                .mb-4
                  h6.mb-2 Readonly State
                  small.text--secondary.d-block.mb-2 Prevents interaction but maintains normal visual appearance. Value: {{ JSON.stringify(readonlyMultipleValue) }}
                  yz-api-dropdown(
                    v-model="readonlyMultipleValue"
                    :items="multipleNoChipsItems"
                    :loading="isMultipleNoChipsLoading"
                    :count="multipleNoChipsTotalCount"
                    :page-size="multipleNoChipsPageSize"
                    :page.sync="multipleNoChipsCurrentPage"
                    label="Readonly Multiple Patient Dropdown"
                    placeholder="Cannot interact..."
                    item-text="name"
                    item-value="id"
                    multiple
                    readonly
                    clearable
                    @opened="fetchMultipleNoChips"
                    @searched="onMultipleNoChipsSearch"
                  )

                .mb-4
                  h6.mb-2 Disabled State
                  small.text--secondary.d-block.mb-2 Prevents interaction with visual indicators (grayed out). Value: {{ JSON.stringify(disabledMultipleValue) }}
                  yz-api-dropdown(
                    v-model="disabledMultipleValue"
                    :items="multipleNoChipsItems"
                    :loading="isMultipleNoChipsLoading"
                    :count="multipleNoChipsTotalCount"
                    :page-size="multipleNoChipsPageSize"
                    :page.sync="multipleNoChipsCurrentPage"
                    label="Disabled Multiple Patient Dropdown"
                    placeholder="Cannot interact..."
                    item-text="name"
                    item-value="id"
                    multiple
                    disabled
                    clearable
                    @opened="fetchMultipleNoChips"
                    @searched="onMultipleNoChipsSearch"
                  )

            v-divider

            // Multiple Selection with Chips Examples
            .mb-6
              h5.mb-3.text-primary Multiple Selection with Chips Examples
              .d-flex.flex-column.gap-4
                .mb-4
                  h6.mb-2 Normal (Interactive)
                  small.text--secondary.d-block.mb-2 Value: {{ JSON.stringify(multipleWithChipsReturnObjectValue) }}
                  yz-api-dropdown(
                    v-model="multipleWithChipsReturnObjectValue"
                    :items="multipleWithChipsReturnObjectItems"
                    :loading="isMultipleWithChipsReturnObjectLoading"
                    :count="multipleWithChipsReturnObjectTotalCount"
                    :page-size="multipleWithChipsReturnObjectPageSize"
                    :page.sync="multipleWithChipsReturnObjectCurrentPage"
                    label="Normal Chips Patient Dropdown"
                    placeholder="Can select multiple patients..."
                    item-text="name"
                    item-value="id"
                    multiple
                    chips
                    clearable
                    @opened="fetchMultipleWithChipsReturnObject"
                    @searched="onMultipleWithChipsReturnObjectSearch"
                  )

                .mb-4
                  h6.mb-2 Readonly State
                  small.text--secondary.d-block.mb-2 Prevents interaction but maintains normal visual appearance. Value: {{ JSON.stringify(readonlyChipsValue) }}
                  yz-api-dropdown(
                    v-model="readonlyChipsValue"
                    :items="multipleWithChipsReturnObjectItems"
                    :loading="isMultipleWithChipsReturnObjectLoading"
                    :count="multipleWithChipsReturnObjectTotalCount"
                    :page-size="multipleWithChipsReturnObjectPageSize"
                    :page.sync="multipleWithChipsReturnObjectCurrentPage"
                    label="Readonly Chips Patient Dropdown"
                    placeholder="Cannot interact..."
                    item-text="name"
                    item-value="id"
                    multiple
                    chips
                    readonly
                    clearable
                    @opened="fetchMultipleWithChipsReturnObject"
                    @searched="onMultipleWithChipsReturnObjectSearch"
                  )

                .mb-4
                  h6.mb-2 Disabled State
                  small.text--secondary.d-block.mb-2 Prevents interaction with visual indicators (grayed out). Value: {{ JSON.stringify(disabledChipsValue) }}
                  yz-api-dropdown(
                    v-model="disabledChipsValue"
                    :items="multipleWithChipsReturnObjectItems"
                    :loading="isMultipleWithChipsReturnObjectLoading"
                    :count="multipleWithChipsReturnObjectTotalCount"
                    :page-size="multipleWithChipsReturnObjectPageSize"
                    :page.sync="multipleWithChipsReturnObjectCurrentPage"
                    label="Disabled Chips Patient Dropdown"
                    placeholder="Cannot interact..."
                    item-text="name"
                    item-value="id"
                    multiple
                    chips
                    disabled
                    clearable
                    @opened="fetchMultipleWithChipsReturnObject"
                    @searched="onMultipleWithChipsReturnObjectSearch"
                  )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === readonlyDisabledCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === readonlyDisabledCode ? 'success' : ''" @click="copyCode(readonlyDisabledCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ readonlyDisabledCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      // Props
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Events
      h4.mb-3 Events
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
                th Parameters
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}
                td.text--secondary.text-no-wrap {{ event.parameters }}

      // Slots
      h4.mb-3 Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
                th Scope
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
                td.text--secondary.text-no-wrap {{ slot.scope }}

    // Usage Notes
    .component-section.mb-8#usage-notes
      h3.mb-4.cursor-pointer(@click="navigateToSection('usage-notes')") Usage Notes

      .mb-4
        h4.mb-2 API Integration Pattern
        p.mb-2 The component follows a reactive pattern where:
        ol.ml-4
          li.mb-1 User clicks dropdown activator
          li.mb-1 Component emits <code>opened</code> event
          li.mb-1 Parent component handles API call and sets <code>loading="true"</code>
          li.mb-1 Parent updates <code>items</code> prop with API response data
          li.mb-1 Parent sets <code>loading="false"</code> and updates pagination props

      .mb-4
        h4.mb-2 Search Functionality
        p.mb-2 The integrated search feature provides:
        ul.ml-4
          li.mb-1 <strong>Server-side filtering:</strong> Emits search events for API integration
          li.mb-1 <strong>Debounced input:</strong> Configurable delay (default 750ms) prevents excessive API calls
          li.mb-1 <strong>Sticky positioning:</strong> Search field remains visible when scrolling
          li.mb-1 <strong>Clearable:</strong> Built-in clear button to reset search
          li.mb-1 <strong>Configurable:</strong> Use <code>searchable="false"</code> to disable
          li.mb-1 <strong>Responsive:</strong> Adapts to different screen sizes

      .mb-4
        h4.mb-2 Example API Response Format
        pre.code-block.pa-3.rounded.text-caption.mb-2(style="background-color: #f5f5f5; overflow-x: auto;")
          code {{ exampleApiResponse }}

      .mb-4
        h4.mb-2 Best Practices
        ul.ml-4
          li.mb-1 Always handle loading states to provide user feedback
          li.mb-1 Use pagination for large datasets to improve performance
          li.mb-1 Implement error handling for failed API requests
          li.mb-1 Consider caching API responses for frequently accessed data
          li.mb-1 Use return-object when you need access to full item data
</template>

<script>

import {mapActions} from "vuex";

export default {
  name: "ApiDropdown",
  data() {
    return {
      copiedCode: null,

      // Basic API example
      basicApiValue: null,
      basicApiItems: [],
      isBasicLoading: false,
      basicTotalCount: 0,
      basicPageSize: 10,
      basicCurrentPage: 1,
      basicSearchQuery: '',

      // Custom template example
      customTemplateValue: null,
      customTemplateItems: [],
      isCustomTemplateLoading: false,
      customTemplateTotalCount: 0,
      customTemplatePageSize: 3,
      customTemplateCurrentPage: 1,
      customTemplateSearchQuery: '',

      // Custom template multiple example
      customTemplateMultipleValue: [],
      customTemplateMultipleItems: [],
      isCustomTemplateMultipleLoading: false,
      customTemplateMultipleTotalCount: 0,
      customTemplateMultiplePageSize: 4,
      customTemplateMultipleCurrentPage: 1,
      customTemplateMultipleSearchQuery: '',

      // Multiple with chips example
      multipleWithChipsReturnObjectValue: [],
      multipleWithChipsReturnObjectItems: [],
      isMultipleWithChipsReturnObjectLoading: false,
      multipleWithChipsReturnObjectTotalCount: 0,
      multipleWithChipsReturnObjectPageSize: 5,
      multipleWithChipsReturnObjectCurrentPage: 1,
      multipleWithChipsReturnObjectSearchQuery: '',

      // Multiple without chips example
      multipleNoChipsValue: [],
      multipleNoChipsItems: [],
      isMultipleNoChipsLoading: false,
      multipleNoChipsTotalCount: 0,
      multipleNoChipsPageSize: 4,
      multipleNoChipsCurrentPage: 1,
      multipleNoChipsSearchQuery: '',

      // Multiple with custom template and chips example
      multipleCustomValue: [],
      multipleCustomItems: [],
      isMultipleCustomLoading: false,
      multipleCustomTotalCount: 0,
      multipleCustomPageSize: 6,
      multipleCustomCurrentPage: 1,
      multipleCustomSearchQuery: '',

      // Clearable examples  
      clearableSingleValue: null,
      clearableSingleItems: [],
      isClearableSingleLoading: false,
      clearableSingleTotalCount: 0,
      clearableSinglePageSize: 5,
      clearableSingleCurrentPage: 1,
      clearableSingleSearchQuery: '',

      clearableMultipleValue: [],
      clearableMultipleItems: [],
      isClearableMultipleLoading: false,
      clearableMultipleTotalCount: 0,
      clearableMultiplePageSize: 5,
      clearableMultipleCurrentPage: 1,
      clearableMultipleSearchQuery: '',

      clearableChipsSingleValue: null,
      clearableChipsSingleItems: [],
      isClearableChipsSingleLoading: false,
      clearableChipsSingleTotalCount: 0,
      clearableChipsSinglePageSize: 5,
      clearableChipsSingleCurrentPage: 1,
      clearableChipsSingleSearchQuery: '',

      clearableChipsMultipleValue: [],
      clearableChipsMultipleItems: [],
      isClearableChipsMultipleLoading: false,
      clearableChipsMultipleTotalCount: 0,
      clearableChipsMultiplePageSize: 5,
      clearableChipsMultipleCurrentPage: 1,
      clearableChipsMultipleSearchQuery: '',

      // Empty state examples
      emptyStateValue: null,
      emptyStateItems: [],
      noResultsValue: null,
      noResultsItems: [],
      noResultsSearchQuery: '',

      // Header and Footer slot examples
      headerValue: null,
      headerItems: [],
      isHeaderLoading: false,
      headerTotalCount: 0,
      headerPageSize: 6,
      headerCurrentPage: 1,
      headerSearchQuery: '',

      footerValue: null,
      footerItems: [],
      isFooterLoading: false,
      footerTotalCount: 0,
      footerPageSize: 5,
      footerCurrentPage: 1,
      footerSearchQuery: '',

      combinedValue: null,
      combinedItems: [],
      isCombinedLoading: false,
      combinedTotalCount: 0,
      combinedPageSize: 4,
      combinedCurrentPage: 1,
      combinedSearchQuery: '',

      // Error state examples
      errorStateValue: null,
      errorStateItems: [],
      isErrorStateLoading: false,
      errorStateError: false,
      errorStateTotalCount: 0,
      errorStatePageSize: 5,
      errorStateCurrentPage: 1,
      errorStateSearchQuery: '',

      customErrorStateValue: null,
      customErrorStateItems: [],
      isCustomErrorStateLoading: false,
      customErrorStateError: false,
      customErrorStateTotalCount: 0,
      customErrorStatePageSize: 5,
      customErrorStateCurrentPage: 1,
      customErrorStateSearchQuery: '',

      slotErrorStateValue: null,
      slotErrorStateItems: [],
      isSlotErrorStateLoading: false,
      slotErrorStateError: false,
      slotErrorStateTotalCount: 0,
      slotErrorStatePageSize: 5,
      slotErrorStateCurrentPage: 1,
      slotErrorStateSearchQuery: '',

      // v-form validation examples
      formValid: false,
      basicFormValue: null,
      basicFormItems: [],
      isBasicFormLoading: false,
      basicFormTotalCount: 0,
      basicFormPageSize: 5,
      basicFormCurrentPage: 1,
      basicFormSearchQuery: '',
      basicFormValidationResult: null,

      customRulesValue: null,
      customRulesItems: [],
      isCustomRulesLoading: false,
      customRulesTotalCount: 0,
      customRulesPageSize: 5,
      customRulesCurrentPage: 1,
      customRulesSearchQuery: '',
      customRulesFormValid: false,
      customRulesValidationResult: null,

      multipleRulesValue: [],
      multipleRulesItems: [],
      isMultipleRulesLoading: false,
      multipleRulesTotalCount: 0,
      multipleRulesPageSize: 5,
      multipleRulesCurrentPage: 1,
      multipleRulesSearchQuery: '',
      multipleRulesFormValid: false,
      multipleRulesValidationResult: null,

      chipsMultipleRulesValue: [],
      chipsRulesItems: [],
      isChipsRulesLoading: false,
      chipsRulesTotalCount: 0,
      chipsRulesPageSize: 5,
      chipsRulesCurrentPage: 1,
      chipsRulesSearchQuery: '',
      chipsMultipleRulesFormValid: false,
      chipsMultipleRulesValidationResult: null,

      // Optional Validation Examples Data
      optionalDepartmentValue: null,
      optionalDepartmentItems: [],
      isOptionalDepartmentLoading: false,
      optionalDepartmentCount: 0,
      optionalDepartmentPage: 1,
      optionalDepartmentValidationResult: null,

      optionalUserValue: null,
      optionalUserItems: [],
      isOptionalUserLoading: false,
      optionalUserCount: 0,
      optionalUserPage: 1,
      optionalUserValidationResult: null,

      optionalProjectValue: null,
      optionalProjectItems: [],
      isOptionalProjectLoading: false,
      optionalProjectCount: 0,
      optionalProjectPage: 1,
      optionalProjectValidationResult: null,

      // Readonly/Disabled State Examples Data
      readonlyFormValue: { id: 2, name: 'Jane Smith', ic: '851122334455' },
      disabledFormValue: { id: 1, name: 'John Doe', ic: '901234567890' },
      readonlyMultipleValue: [
        { id: 1, name: 'John Doe', ic: '901234567890' },
        { id: 3, name: 'Bob Johnson', ic: '771122334455' }
      ],
      disabledMultipleValue: [
          { id: 2, name: 'Jane Smith', ic: '851122334455' },
          { id: 4, name: 'Alice Brown', ic: '661122334455' }
      ],
      readonlyChipsValue: [
        { id: 1, name: 'John Doe', ic: '901234567890' },
        { id: 2, name: 'Jane Smith', ic: '851122334455' }
      ],
      disabledChipsValue: [
          { id: 3, name: 'Bob Johnson', ic: '771122334455' },
          { id: 4, name: 'Alice Brown', ic: '661122334455' }
      ]
    }
  },
  computed: {
    basicApiCode() {
      return `// Template
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  searchable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Script
data() {
  return {
    selectedValue: null,
    apiItems: [],
    isLoading: false,
    totalCount: 0,
    pageSize: 10,
    currentPage: 1,
    searchQuery: ''
  }
},
methods: {
  async fetchPatients() {
    this.isLoading = true
    try {
      const params = {
        fields: 'id,name,phone,ic,profile_image',
        search: this.searchQuery || '',
        page: this.currentPage,
        ordering: '-created'
      }
      
      const response = await this.$axios.get('/clinics/codeblue/patients', { params })
      this.apiItems = response.data.results
      this.totalCount = response.data.count
    } catch (error) {
      console.error('Failed to fetch patients:', error)
    } finally {
      this.isLoading = false
    }
  },
  onSearch(searchTerm) {
    this.searchQuery = searchTerm
    this.currentPage = 1 // Reset to first page
    this.fetchPatients()
  }
}`
    },

    customTemplateCode() {
      return `yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient (Custom Template)"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  return-object
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#item="{ item }")
    .d-flex.justify-space-between.align-center
      div
        span {{ item.name }}
        .yz-regular-body-xs.text-neutral-500 {{ item.ic }}
      yz-btn(icon-only icon="mdi-trash-can" icon-color="danger" @click.stop="onClickDelete(item)")`
    },

    customTemplateMultipleCode() {
      return `yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Multiple Patients (Custom Template)"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  return-object
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#item="{ item }")
    .d-flex.justify-space-between.align-center
      div
        span {{ item.name }}
        .yz-regular-body-xs.text-neutral-500 {{ item.ic }}
      yz-btn(icon-only icon="mdi-trash-can" icon-color="danger" @click.stop="onClickDelete(item)")`
    },


    multipleWithChipsReturnObjectCode() {
      return `yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Multiple Patients"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  chips
  @opened="fetchPatients"
  @searched="onSearch"
)`
    },

    multipleNoChipsCode() {
      return `yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Multiple Patients"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  @opened="fetchPatients"
  @searched="onSearch"
)`
    },


    multipleCustomCode() {
      return `yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Multiple Patients"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  chips
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#item="{ item }")
    .d-flex.align-center
      .mr-3
        v-avatar(size="32")
          yz-img(v-if="item.profile_image" :src="item.profile_image")
          yz-icon(v-else) mdi-account
      .d-flex.flex-column
        .font-weight-medium {{ item.name }}
        .text-caption.text--secondary {{ item.role || 'Patient' }}`
    },

    emptyStateCode() {
      return `// No Data State Example
yz-api-dropdown(
  v-model="selectedValue"
  :items="emptyItems"
  :loading="false"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  no-data-text="Custom no data message"
  searchable
)
  template(#no-data)
    .pa-6.text-center
      yz-icon.mb-2(size="48" color="neutral-400") mdi-account-off
      .yz-regular-body-md.text-neutral-600.mb-2 No patients available
      .text-caption.text-neutral-500 Try adding some patients first
      yz-btn.mt-3(size="sm" variant="outline") Add Patient`
    },

    noResultsCode() {
      return `// No Results State Example
yz-api-dropdown(
  v-model="selectedValue"
  :items="filteredItems"
  :loading="false"
  label="Search Patients"
  placeholder="Type to search..."
  item-text="name"
  item-value="id"
  no-results-text="Custom no results message"
  searchable
  @searched="onSearch"
)
  template(#no-results)
    .pa-6.text-center
      yz-icon.mb-2(size="48" color="neutral-400") mdi-magnify-close
      .yz-regular-body-md.text-neutral-600.mb-2 No matching patients found
      .text-caption.text-neutral-500 Try adjusting your search criteria
      yz-btn.mt-3(size="sm" variant="outline") Clear Search

// Methods
onSearch(searchTerm) {
  if (searchTerm === 'xyz') {
    this.filteredItems = [] // Simulate no results
  } else {
    // Your normal API call logic here
    this.fetchPatients(searchTerm)
  }
}`
    },

    headerSlotCode() {
      return `// Header Slot Example
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  searchable
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#header)
    .pa-3.border-bottom.bg-primary-50
      .d-flex.justify-space-between.align-center
        .d-flex.align-center
          yz-icon.mr-2(size="sm" color="primary") mdi-account-group
          span.yz-regular-body-sm.text-primary-700 {{ totalCount }} patients available
          span.ml-2.text-caption.text-neutral-500(v-if="searchQuery") (filtered by "{{ searchQuery }}")
        yz-btn(size="sm" icon="mdi-plus" variant="primary" @click="addNewPatient") Add Patient

// Methods
addNewPatient() {
  this.showMessage(['success', 'Add patient dialog would open'])
}`
    },

    footerSlotCode() {
      return `// Footer Slot Example  
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  searchable
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#footer)
    .pa-3.border-top.bg-neutral-50
      .text-center
        .text-caption.text-neutral-600.mb-2
          | Showing {{ apiItems.length }} of {{ totalCount }} patients
          span(v-if="selectedValue") • 1 selected
        .d-flex.justify-center.gap-2
          yz-btn(size="sm" variant="text" icon="mdi-eye") View All
          yz-btn(size="sm" variant="text" icon="mdi-download") Export List`
    },

    combinedSlotCode() {
      return `// Combined Header and Footer Slots
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  searchable
  return-object
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#header)
    .pa-3.border-bottom.bg-primary-50
      .d-flex.justify-space-between.align-center
        .d-flex.align-center
          yz-icon.mr-2(size="sm" color="primary") mdi-account-group
          span.yz-regular-body-sm.text-primary-700 {{ totalCount }} patients available
          span.ml-2.text-caption.text-neutral-500(v-if="searchQuery") (filtered by "{{ searchQuery }}")
        yz-btn(size="sm" icon="mdi-plus" variant="primary" @click="addNewPatient") Add Patient
  template(#footer)
    .pa-3.border-top.bg-neutral-50
      .text-center
        .text-caption.text-neutral-600.mb-2
          | Showing {{ apiItems.length }} of {{ totalCount }} patients
          span(v-if="selectedValue") • 1 selected
        .d-flex.justify-center.gap-2
          yz-btn(size="sm" variant="text" icon="mdi-eye") View All
          yz-btn(size="sm" variant="text" icon="mdi-download") Export List`
    },

    exampleApiResponse() {
      return `{
  "count": 125,
  "next": "?page=2",
  "previous": null,
  "results": [
    {
      "id": 1,
      "name": "John Doe",
      "phone": "+60123456789",
      "ic": "901234567890",
      "profile_image": null
    },
    {
      "id": 2,
      "name": "Jane Smith",
      "phone": "+60198765432",
      "ic": "851122334455",
      "profile_image": "https://example.com/profile.jpg"
    }
    // ... more patients
  ]
}`
    },

    errorStateCode() {
      return `// Default Error State Example
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :error="hasError"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  @opened="fetchPatients"
  @searched="onSearch"
)

// Methods
async fetchPatients() {
  this.isLoading = true
  this.hasError = false
  
  try {
    const response = await this.$axios.get('/clinics/codeblue/patients')
    this.apiItems = response.data.results
    this.totalCount = response.data.count
  } catch (error) {
    console.error('API Error:', error)
    this.hasError = true // Show error state
    this.apiItems = []
  } finally {
    this.isLoading = false
  }
}`
    },

    customErrorCode() {
      return `// Custom Error Message Example
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :error="hasError"
  error-message="Network connection failed. Please check your internet connection and try again."
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  @opened="fetchPatients"
  @searched="onSearch"
)`
    },

    customSlotErrorCode() {
      return `// Custom Error Slot Example
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :error="hasError"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  @opened="fetchPatients"
  @searched="onSearch"
)
  template(#error)
    .pa-6.text-center
      yz-icon.mb-3(size="48" color="danger") mdi-alert-circle
      .yz-regular-body-md.text-danger-600.mb-2 Server Error Occurred
      .text-caption.text-neutral-600.mb-4 Unable to load patient data. Our servers are experiencing issues.
      .d-flex.justify-center.gap-2
        yz-btn(size="sm" variant="outline" label="Try Again" @click="retryFetch")
        yz-btn(size="sm" variant="text" label="Contact Support" @click="contactSupport")

// Methods
retryFetch() {
  this.fetchPatients()
},
contactSupport() {
  this.showMessage(['info', 'Contact support functionality would be triggered'])
}`
    },

    clearableSingleCode() {
      return `// Clearable Single Selection
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient (Clearable)"
  placeholder="Choose a patient..."
  item-text="name"
  item-value="id"
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Methods
async fetchPatients() {
  this.isLoading = true
  try {
    const params = {
      fields: 'id,name,phone,ic,profile_image',
      search: this.searchQuery || '',
      page: this.currentPage,
      ordering: '-created'
    }
    const response = await this.$axios.get('/clinics/codeblue/patients', { params })
    this.apiItems = response.data.results
    this.totalCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isLoading = false
  }
}`
    },

    clearableMultipleCode() {
      return `// Clearable Multiple Selection
yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patients (Clearable)"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Methods
async fetchPatients() {
  this.isLoading = true
  try {
    const params = {
      fields: 'id,name,phone,ic,profile_image',
      search: this.searchQuery || '',
      page: this.currentPage,
      ordering: '-created'
    }
    const response = await this.$axios.get('/clinics/codeblue/patients', { params })
    this.apiItems = response.data.results
    this.totalCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isLoading = false
  }
}`
    },

    clearableChipsSingleCode() {
      return `// Clearable Chips Single Selection
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patient (Clearable Chips)"
  placeholder="Choose a patient..."
  item-text="name"
  item-value="id"
  chips
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Methods
async fetchPatients() {
  this.isLoading = true
  try {
    const params = {
      fields: 'id,name,phone,ic,profile_image',
      search: this.searchQuery || '',
      page: this.currentPage,
      ordering: '-created'
    }
    const response = await this.$axios.get('/clinics/codeblue/patients', { params })
    this.apiItems = response.data.results
    this.totalCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isLoading = false
  }
}`
    },

    clearableChipsMultipleCode() {
      return `// Clearable Chips Multiple Selection
yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Select Patients (Clearable Chips)"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  chips
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Methods
async fetchPatients() {
  this.isLoading = true
  try {
    const params = {
      fields: 'id,name,phone,ic,profile_image',
      search: this.searchQuery || '',
      page: this.currentPage,
      ordering: '-created'
    }
    const response = await this.$axios.get('/clinics/codeblue/patients', { params })
    this.apiItems = response.data.results
    this.totalCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isLoading = false
  }
}`
    },

    vformValidationCode() {
      return `// Basic v-form with required validation
v-form(ref="form" v-model="formValid")
  yz-api-dropdown(
    v-model="selectedValue"
    :items="apiItems"
    :loading="isLoading"
    :count="totalCount"
    :page-size="pageSize"
    :page.sync="currentPage"
    label="Select Patient"
    placeholder="Choose a patient..."
    item-text="name"
    item-value="id"
    required
    required-error-message="Please select a patient"
    @opened="fetchPatients"
    @searched="onSearch"
  )
  yz-btn(:disabled="!formValid" @click="submit") Submit

// Custom validation rules with API
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :rules="[v => v?.id !== items[0]?.id || 'Cannot select first patient']"
  label="Custom Rules Dropdown"
  item-text="name"
  item-value="id"
  @opened="fetchPatients"
  @searched="onSearch"
)

// Multiple selection with rules
yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :rules="[
    v => v.length >= 2 || 'Select at least 2 patients',
    v => v.length <= 3 || 'Select maximum 3 patients'
  ]"
  label="Multiple with Rules"
  placeholder="Select 2-3 patients..."
  item-text="name"
  item-value="id"
  multiple
  @opened="fetchPatients"
  @searched="onSearch"
)

// Multiple selection with chips validation
yz-api-dropdown(
  v-model="selectedPatients"
  :items="apiItems"
  :loading="isLoading"
  :rules="[v => v.length > 0 || 'Select at least one patient']"
  label="Select Patients"
  placeholder="Choose patients..."
  item-text="name"
  item-value="id"
  multiple
  chips
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)`
    },

    // Custom validation rules
    customApiDropdownRules() {
      return [
        v => v?.id !== this.customRulesItems[0]?.id || 'Cannot select first patient (try others)'
      ]
    },
    multipleApiDropdownRules() {
      return [
        v => v.length >= 2 || 'Select at least 2 patients',
        v => v.length <= 3 || 'Select maximum 3 patients'
      ]
    },
    chipsApiRules() {
      return [
        v => v.length > 0 || 'Select at least one patient'
      ]
    },
    componentProps() {
      return [
        // All YzDropdown props
        {name: 'value', type: 'Any', default: 'null', description: 'The selected value(s) for v-model binding'},
        {name: 'items', type: 'Array', default: '[]', description: 'Array of dropdown options'},
        {
          name: 'item-text',
          type: 'String|Function',
          default: "'text'",
          description: 'Property name or function to get display text'
        },
        {
          name: 'item-value',
          type: 'String|Function',
          default: "'value'",
          description: 'Property name or function to get item value'
        },
        {name: 'multiple', type: 'Boolean', default: 'false', description: 'Allow multiple selection'},
        {name: 'chips', type: 'Boolean', default: 'false', description: 'Display selections as chips'},
        {name: 'size', type: 'String', default: "'lg'", description: 'Component size: sm, md, lg'},
        {name: 'disabled', type: 'Boolean', default: 'false', description: 'Disable the dropdown'},
        {name: 'clearable', type: 'Boolean', default: 'false', description: 'Show clear icon'},
        {name: 'width', type: 'String', default: "'auto'", description: 'Component width'},

        // API-specific props
        {name: 'loading', type: 'Boolean', default: 'false', description: 'Show loading state'},
        {name: 'error', type: 'Boolean', default: 'false', description: 'Show error state when API calls fail'},
        {
          name: 'error-message',
          type: 'String',
          default: "'Failed to fetch data. Please try again'",
          description: 'Custom error message text'
        },
        {name: 'required', type: 'Boolean', default: 'false', description: 'Make field required for validation'},
        {
          name: 'required-error-message',
          type: 'String',
          default: "'This field is required'",
          description: 'Custom error message to show when required validation fails'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation functions. Each function receives the value and returns either true (valid) or a string (error message)'
        },
        {name: 'searchable', type: 'Boolean', default: 'true', description: 'Show search field above items'},
        {
          name: 'debounce-time',
          type: 'Number',
          default: '750',
          description: 'Debounce delay for search in milliseconds'
        },
        {name: 'count', type: 'Number', default: '0', description: 'Total number of items for pagination'},
        {name: 'page-size', type: 'Number', default: '10', description: 'Number of items per page'},
        {name: 'page', type: 'Number', default: '1', description: 'Current page number'},
        {
          name: 'pagination-type',
          type: 'String',
          default: "'item'",
          description: 'Pagination display type: page, item'
        },
        {name: 'pagination-text', type: 'String', default: "'items'", description: 'Text shown in pagination'},
        {
          name: 'pagination-arrow',
          type: 'Boolean',
          default: 'true',
          description: 'Show arrow buttons instead of text buttons'
        },
        {
          name: 'pagination-change-page-size',
          type: 'Boolean',
          default: 'false',
          description: 'Allow changing page size'
        },
        {name: 'show-pagination', type: 'Boolean', default: 'true', description: 'Show pagination component'},
        {
          name: 'pagination-class',
          type: 'String',
          default: "'px-3 pb-2'",
          description: 'CSS classes for pagination wrapper'
        },
        {
          name: 'no-data-text',
          type: 'String',
          default: "'No results found'",
          description: 'Text displayed when no items are provided'
        },
        {
          name: 'no-results-text',
          type: 'String',
          default: "'No results found'",
          description: 'Text displayed when search yields no results'
        },
        {
          name: 'readonly',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the dropdown readonly (prevents interaction but maintains normal visual appearance, unlike disabled)'
        }
      ]
    },

    componentEvents() {
      return [
        {name: 'input', description: 'Emitted when selection changes (v-model)', parameters: 'value: Any'},
        {name: 'change', description: 'Emitted when selection changes', parameters: 'value: Any'},
        {name: 'clear', description: 'Emitted when selection is cleared', parameters: 'none'},
        {name: 'opened', description: 'Emitted when dropdown menu opens - trigger API call here', parameters: 'none'},
        {name: 'update:page', description: 'Emitted when page changes', parameters: 'page: Number'},
        {name: 'update:pageSize', description: 'Emitted when page size changes', parameters: 'pageSize: Number'},
        {
          name: 'searched',
          description: 'Emitted when search term changes (debounced, default 750ms)',
          parameters: 'searchTerm: String'
        }
      ]
    },

    componentSlots() {
      return [
        {name: 'header', description: 'Content shown above the item list', scope: 'none'},
        {name: 'footer', description: 'Content shown below the item list', scope: 'none'},
        {name: 'loader', description: 'Custom loading indicator', scope: 'none'},
        {name: 'error', description: 'Custom error content when API calls fail', scope: 'none'},
        {name: 'item', description: 'Custom item template', scope: '{ item, index }'},
        {
          name: 'no-data',
          description: 'Content shown when no items are provided (items array is empty)',
          scope: 'none'
        },
        {
          name: 'no-results',
          description: 'Content shown when search is provided but no results are found',
          scope: 'none'
        }
      ]
    },
    // Optional Validation Rules
    optionalDepartmentRules() {
      return [
        v => !v || this.optionalDepartmentItems.some(item => item.id === v) || 'Please select a valid patient for department'
      ]
    },
    optionalUserRules() {
      return [
        v => !v || this.optionalUserItems.some(item => item.id === v) || 'Please select a valid patient for user assignment'
      ]
    },
    optionalProjectRules() {
      return [
        v => !v || this.optionalProjectItems.some(item => item.id === v) || 'Please select a valid patient for project'
      ]
    },

    // Code examples for optional validation
    optionalDepartmentCode() {
      return `yz-api-dropdown(
  v-model="departmentFilter"
  :items="patientItems"
  :loading="isLoading"
  :count="patientCount"
  :page-size="10"
  :page.sync="patientPage"
  :rules="optionalDepartmentRules"
  label="Patient Assignment (optional)"
  placeholder="Select patient..."
  item-text="name"
  item-value="id"
  clearable
  @opened="fetchPatients"
  @searched="onPatientSearch"
)

// Validation rule
optionalDepartmentRules() {
  return [
    v => !v || this.patientItems.some(item => item.id === v) || 'Please select a valid patient'
  ]
}

// API Methods
async fetchPatients() {
  this.isLoading = true
  try {
    const response = await this.$axios.get('/clinics/codeblue/patients', {
      params: { 
        fields: 'id,name,phone,ic,profile_image',
        page: this.patientPage,
        ordering: '-created'
      }
    })
    this.patientItems = response.data.results
    this.patientCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isLoading = false
  }
}`
    },
    optionalUserCode() {
      return `yz-api-dropdown(
  v-model="assignedUser"
  :items="patientItems"
  :loading="isPatientLoading"
  :count="patientCount"
  :page-size="15"
  :page.sync="patientPage"
  :rules="optionalUserRules"
  label="Assign Patient (optional)"
  placeholder="Choose patient..."
  item-text="name"
  item-value="id"
  clearable
  @opened="fetchPatients"
  @searched="onPatientSearch"
)

// Validation rule
optionalUserRules() {
  return [
    v => !v || this.patientItems.some(item => item.id === v) || 'Please select a valid patient'
  ]
}

// API Methods
async fetchPatients() {
  this.isPatientLoading = true
  try {
    const response = await this.$axios.get('/clinics/codeblue/patients', {
      params: { 
        fields: 'id,name,phone,ic,profile_image',
        page: this.patientPage, 
        ordering: '-created'
      }
    })
    this.patientItems = response.data.results
    this.patientCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isPatientLoading = false
  }
}`
    },
    optionalProjectCode() {
      return `yz-api-dropdown(
  v-model="selectedProject"
  :items="patientItems"
  :loading="isPatientLoading"
  :count="patientCount"
  :page-size="20"
  :page.sync="patientPage"
  :rules="optionalProjectRules"
  label="Patient Project (optional)"
  placeholder="Select patient..."
  item-text="name"
  item-value="id"
  clearable
  @opened="fetchPatients"
  @searched="onPatientSearch"
)

// Validation rule
optionalProjectRules() {
  return [
    v => !v || this.patientItems.some(item => item.id === v) || 'Please select a valid patient'
  ]
}

// API Methods
async fetchPatients() {
  this.isPatientLoading = true
  try {
    const response = await this.$axios.get('/clinics/codeblue/patients', {
      params: { 
        fields: 'id,name,phone,ic,profile_image',
        page: this.patientPage, 
        ordering: '-created'
      }
    })
    this.patientItems = response.data.results.map(item => ({
      ...item,
      title: item.name
    }))
    this.patientCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isPatientLoading = false
  }
}`
    },

    readonlyDisabledCode() {
      return `// ===== SINGLE SELECTION EXAMPLES (API-driven) =====

// Normal (Interactive) API Dropdown
yz-api-dropdown(
  v-model="selectedValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Normal Patient Dropdown"
  placeholder="Can interact and select..."
  item-text="name"
  item-value="id"
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Readonly State - maintains visual appearance, prevents interaction
yz-api-dropdown(
  v-model="readonlyValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Readonly Patient Dropdown"
  placeholder="Cannot interact..."
  item-text="name"
  item-value="id"
  readonly
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Disabled State - grayed out appearance, prevents interaction
yz-api-dropdown(
  v-model="disabledValue"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Disabled Patient Dropdown"
  placeholder="Cannot interact..."
  item-text="name"
  item-value="id"
  disabled
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// ===== MULTIPLE SELECTION EXAMPLES (without chips) =====

// Normal Multiple Selection
yz-api-dropdown(
  v-model="selectedValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Normal Multiple Patient Dropdown"
  placeholder="Can select multiple..."
  item-text="name"
  item-value="id"
  multiple
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Readonly Multiple Selection
yz-api-dropdown(
  v-model="readonlyMultipleValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Readonly Multiple Patient Dropdown"
  placeholder="Cannot interact..."
  item-text="name"
  item-value="id"
  multiple
  readonly
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Disabled Multiple Selection
yz-api-dropdown(
  v-model="disabledMultipleValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Disabled Multiple Patient Dropdown"
  placeholder="Cannot interact..."
  item-text="name"
  item-value="id"
  multiple
  disabled
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// ===== MULTIPLE SELECTION WITH CHIPS EXAMPLES =====

// Normal Multiple Selection with Chips
yz-api-dropdown(
  v-model="selectedChipsValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Normal Chips Patient Dropdown"
  placeholder="Can select multiple patients..."
  item-text="name"
  item-value="id"
  multiple
  chips
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Readonly Multiple Selection with Chips
yz-api-dropdown(
  v-model="readonlyChipsValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Readonly Chips Patient Dropdown"
  placeholder="Cannot interact..."
  item-text="name"
  item-value="id"
  multiple
  chips
  readonly
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// Disabled Multiple Selection with Chips
yz-api-dropdown(
  v-model="disabledChipsValues"
  :items="apiItems"
  :loading="isLoading"
  :count="totalCount"
  :page-size="pageSize"
  :page.sync="currentPage"
  label="Disabled Chips Patient Dropdown"
  placeholder="Cannot interact..."
  item-text="name"
  item-value="id"
  multiple
  chips
  disabled
  clearable
  @opened="fetchPatients"
  @searched="onSearch"
)

// ===== API INTEGRATION METHODS =====

// API Methods for fetching data
async fetchPatients() {
  this.isLoading = true
  try {
    const params = {
      fields: 'id,name,phone,ic,profile_image',
      search: this.searchQuery || '',
      page: this.currentPage,
      ordering: '-created'
    }
    const response = await this.$axios.get('/clinics/codeblue/patients', { params })
    this.apiItems = response.data.results
    this.totalCount = response.data.count
  } catch (error) {
    console.error('Failed to fetch patients:', error)
  } finally {
    this.isLoading = false
  }
}

onSearch(searchTerm) {
  this.searchQuery = searchTerm
  this.currentPage = 1 // Reset to first page
  this.fetchPatients()
}`
    },
    watch: {
      basicCurrentPage() {
        if (this.basicApiItems.length > 0) {
          this.fetchPatients()
        }
      },
      customTemplateCurrentPage() {
        if (this.customTemplateItems.length > 0) {
          this.fetchPatientsCustomTemplate()
        }
      },
      customTemplateMultipleCurrentPage() {
        if (this.customTemplateMultipleItems.length > 0) {
          this.fetchPatientsCustomTemplateMultiple()
        }
      },
      multipleWithChipsReturnObjectCurrentPage() {
        if (this.multipleWithChipsReturnObjectItems.length > 0) {
          this.fetchMultipleWithChipsReturnObject()
        }
      },
      multipleNoChipsCurrentPage() {
        if (this.multipleNoChipsItems.length > 0) {
          this.fetchMultipleNoChips()
        }
      },
      multipleCustomCurrentPage() {
        if (this.multipleCustomItems.length > 0) {
          this.fetchMultipleCustom()
        }
      },
      headerCurrentPage() {
        if (this.headerItems.length > 0) {
          this.fetchHeaderPatients()
        }
      },
      footerCurrentPage() {
        if (this.footerItems.length > 0) {
          this.fetchFooterPatients()
        }
      },
      combinedCurrentPage() {
        if (this.combinedItems.length > 0) {
          this.fetchCombinedPatients()
        }
      },
      clearableSingleCurrentPage() {
        if (this.clearableSingleItems.length > 0) {
          this.fetchClearableSingle()
        }
      },
      clearableMultipleCurrentPage() {
        if (this.clearableMultipleItems.length > 0) {
          this.fetchClearableMultiple()
        }
      },
      clearableChipsSingleCurrentPage() {
        if (this.clearableChipsSingleItems.length > 0) {
          this.fetchClearableChipsSingle()
        }
      },
      clearableChipsMultipleCurrentPage() {
        if (this.clearableChipsMultipleItems.length > 0) {
          this.fetchClearableChipsMultiple()
        }
      },
      '$route.hash':
          {
            handler() {
              this.scrollToSection()
            }
            ,
            immediate: false
          }
    }
  },

  mounted() {
    // Initialize no results example with some data
    this.noResultsItems = [
      {id: 1, name: 'John Doe', ic: '901234567890'},
      {id: 2, name: 'Jane Smith', ic: '851122334455'},
      {id: 3, name: 'Bob Johnson', ic: '771122334455'},
      {id: 4, name: 'Alice Brown', ic: '881122334455'}
    ]

    this.$nextTick(() => {
      this.scrollToSection()
    })
  },

  methods: {
    ...mapActions(['showMessage']),

    // Error state example methods
    simulateApiError() {
      this.errorStateError = true
      this.errorStateItems = []
      this.errorStateTotalCount = 0
    },

    resetErrorState() {
      this.errorStateError = false
      this.errorStateItems = []
      this.errorStateTotalCount = 0
    },

    async fetchErrorStatePatients() {
      if (this.errorStateError) return // Don't fetch if in error state

      this.isErrorStateLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.errorStateSearchQuery || '',
          page: this.errorStateCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.errorStateItems = response.data.results
        this.errorStateTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch error state patients:', error)
        this.errorStateError = true
        this.errorStateItems = []
        this.errorStateTotalCount = 0
      } finally {
        this.isErrorStateLoading = false
      }
    },

    onErrorStateSearch(searchTerm) {
      this.errorStateSearchQuery = searchTerm
      this.errorStateCurrentPage = 1
      this.fetchErrorStatePatients()
    },

    // Custom error state methods
    simulateCustomError() {
      this.customErrorStateError = true
      this.customErrorStateItems = []
      this.customErrorStateTotalCount = 0
    },

    resetCustomError() {
      this.customErrorStateError = false
      this.customErrorStateItems = []
      this.customErrorStateTotalCount = 0
    },

    async fetchCustomErrorStatePatients() {
      if (this.customErrorStateError) return

      this.isCustomErrorStateLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.customErrorStateSearchQuery || '',
          page: this.customErrorStateCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.customErrorStateItems = response.data.results
        this.customErrorStateTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch custom error state patients:', error)
        this.customErrorStateError = true
        this.customErrorStateItems = []
        this.customErrorStateTotalCount = 0
      } finally {
        this.isCustomErrorStateLoading = false
      }
    },

    onCustomErrorStateSearch(searchTerm) {
      this.customErrorStateSearchQuery = searchTerm
      this.customErrorStateCurrentPage = 1
      this.fetchCustomErrorStatePatients()
    },

    // Slot error state methods
    simulateSlotError() {
      this.slotErrorStateError = true
      this.slotErrorStateItems = []
      this.slotErrorStateTotalCount = 0
    },

    resetSlotError() {
      this.slotErrorStateError = false
      this.slotErrorStateItems = []
      this.slotErrorStateTotalCount = 0
    },

    async fetchSlotErrorStatePatients() {
      if (this.slotErrorStateError) return

      this.isSlotErrorStateLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.slotErrorStateSearchQuery || '',
          page: this.slotErrorStateCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.slotErrorStateItems = response.data.results
        this.slotErrorStateTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch slot error state patients:', error)
        this.slotErrorStateError = true
        this.slotErrorStateItems = []
        this.slotErrorStateTotalCount = 0
      } finally {
        this.isSlotErrorStateLoading = false
      }
    },

    onSlotErrorStateSearch(searchTerm) {
      this.slotErrorStateSearchQuery = searchTerm
      this.slotErrorStateCurrentPage = 1
      this.fetchSlotErrorStatePatients()
    },

    retrySlotError() {
      this.slotErrorStateError = false
      this.fetchSlotErrorStatePatients()
    },

    contactSupport() {
      this.showMessage(['info', 'Contact support functionality would be triggered here'])
    },
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },

    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },

    async fetchPatients() {
      this.isBasicLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.basicSearchQuery || '',
          page: this.basicCurrentPage,
          ordering: '-created'
        }

        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.basicApiItems = response.data.results
        this.basicTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch patients:', error)
      } finally {
        this.isBasicLoading = false
      }
    },

    onSearch(searchTerm) {
      // Store search query
      this.basicSearchQuery = searchTerm
      // Reset to first page when searching
      this.basicCurrentPage = 1
      // Trigger API call with search term
      this.fetchPatients()
    },

    async fetchPatientsCustomTemplate() {
      this.isCustomTemplateLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.customTemplateSearchQuery || '',
          page: this.customTemplateCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.customTemplateItems = response.data.results
        this.customTemplateTotalCount = response.data.count
      } finally {
        this.isCustomTemplateLoading = false
      }
    },


    async fetchMultipleWithChipsReturnObject() {
      this.isMultipleWithChipsReturnObjectLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.multipleWithChipsReturnObjectSearchQuery || '',
          page: this.multipleWithChipsReturnObjectCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.multipleWithChipsReturnObjectItems = response.data.results
        this.multipleWithChipsReturnObjectTotalCount = response.data.count
      } finally {
        this.isMultipleWithChipsReturnObjectLoading = false
      }
    },

    async fetchMultipleNoChips() {
      this.isMultipleNoChipsLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.multipleNoChipsSearchQuery || '',
          page: this.multipleNoChipsCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.multipleNoChipsItems = response.data.results
        this.multipleNoChipsTotalCount = response.data.count
      } finally {
        this.isMultipleNoChipsLoading = false
      }
    },


    async fetchMultipleCustom() {
      this.isMultipleCustomLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.multipleCustomSearchQuery || '',
          page: this.multipleCustomCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.multipleCustomItems = response.data.results.map(item => ({
          ...item,
          role: item.role || 'Patient'
        }))
        this.multipleCustomTotalCount = response.data.count
      } finally {
        this.isMultipleCustomLoading = false
      }
    },

    onCustomTemplateSearch(searchTerm) {
      this.customTemplateSearchQuery = searchTerm
      this.customTemplateCurrentPage = 1
      this.fetchPatientsCustomTemplate()
    },

    onClickCustomTemplateDelete(item) {
      this.showMessage(['error', `Delete ${item.name} clicked`])
    },

    async fetchPatientsCustomTemplateMultiple() {
      this.isCustomTemplateMultipleLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.customTemplateMultipleSearchQuery || '',
          page: this.customTemplateMultipleCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.customTemplateMultipleItems = response.data.results
        this.customTemplateMultipleTotalCount = response.data.count
      } finally {
        this.isCustomTemplateMultipleLoading = false
      }
    },

    onCustomTemplateMultipleSearch(searchTerm) {
      this.customTemplateMultipleSearchQuery = searchTerm
      this.customTemplateMultipleCurrentPage = 1
      this.fetchPatientsCustomTemplateMultiple()
    },

    onClickCustomTemplateMultipleDelete(item) {
      this.showMessage(['error', `Delete ${item.name} clicked (Multiple)`])
    },


    onMultipleWithChipsReturnObjectSearch(searchTerm) {
      this.multipleWithChipsReturnObjectSearchQuery = searchTerm
      this.multipleWithChipsReturnObjectCurrentPage = 1
      this.fetchMultipleWithChipsReturnObject()
    },

    onMultipleNoChipsSearch(searchTerm) {
      this.multipleNoChipsSearchQuery = searchTerm
      this.multipleNoChipsCurrentPage = 1
      this.fetchMultipleNoChips()
    },


    onMultipleCustomSearch(searchTerm) {
      this.multipleCustomSearchQuery = searchTerm
      this.multipleCustomCurrentPage = 1
      this.fetchMultipleCustom()
    },

    // Clearable methods
    async fetchClearableSingle() {
      this.isClearableSingleLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.clearableSingleSearchQuery || '',
          page: this.clearableSingleCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.clearableSingleItems = response.data.results
        this.clearableSingleTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch patients:', error)
      } finally {
        this.isClearableSingleLoading = false
      }
    },

    onClearableSingleSearch(searchTerm) {
      this.clearableSingleSearchQuery = searchTerm
      this.clearableSingleCurrentPage = 1
      this.fetchClearableSingle()
    },

    async fetchClearableMultiple() {
      this.isClearableMultipleLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.clearableMultipleSearchQuery || '',
          page: this.clearableMultipleCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.clearableMultipleItems = response.data.results
        this.clearableMultipleTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch patients:', error)
      } finally {
        this.isClearableMultipleLoading = false
      }
    },

    onClearableMultipleSearch(searchTerm) {
      this.clearableMultipleSearchQuery = searchTerm
      this.clearableMultipleCurrentPage = 1
      this.fetchClearableMultiple()
    },

    async fetchClearableChipsSingle() {
      this.isClearableChipsSingleLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.clearableChipsSingleSearchQuery || '',
          page: this.clearableChipsSingleCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.clearableChipsSingleItems = response.data.results
        this.clearableChipsSingleTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch patients:', error)
      } finally {
        this.isClearableChipsSingleLoading = false
      }
    },

    onClearableChipsSingleSearch(searchTerm) {
      this.clearableChipsSingleSearchQuery = searchTerm
      this.clearableChipsSingleCurrentPage = 1
      this.fetchClearableChipsSingle()
    },

    async fetchClearableChipsMultiple() {
      this.isClearableChipsMultipleLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.clearableChipsMultipleSearchQuery || '',
          page: this.clearableChipsMultipleCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.clearableChipsMultipleItems = response.data.results
        this.clearableChipsMultipleTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch patients:', error)
      } finally {
        this.isClearableChipsMultipleLoading = false
      }
    },

    onClearableChipsMultipleSearch(searchTerm) {
      this.clearableChipsMultipleSearchQuery = searchTerm
      this.clearableChipsMultipleCurrentPage = 1
      this.fetchClearableChipsMultiple()
    },

    // Empty state methods
    async onNoResultsSearch(searchTerm) {
      this.noResultsSearchQuery = searchTerm

      if (searchTerm === 'xyz') {
        // Simulate no results for demonstration
        this.noResultsItems = []
      } else if (searchTerm && searchTerm.length > 0) {
        // Simulate API call with some results
        try {
          const params = {
            fields: 'id,name,phone,ic,profile_image',
            search: searchTerm,
            page: 1,
            ordering: '-created'
          }
          const response = await this.$axios.get('/clinics/codeblue/patients', {params})
          this.noResultsItems = response.data.results
        } catch (error) {
          console.error('Failed to fetch patients:', error)
          this.noResultsItems = []
        }
      } else {
        // Reset to show some default items when no search
        this.noResultsItems = [
          {id: 1, name: 'John Doe', ic: '901234567890'},
          {id: 2, name: 'Jane Smith', ic: '851122334455'},
          {id: 3, name: 'Bob Johnson', ic: '771122334455'},
          {id: 4, name: 'Alice Brown', ic: '881122334455'}
        ]
      }
    },

    // Header slot example methods
    async fetchHeaderPatients() {
      this.isHeaderLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.headerSearchQuery || '',
          page: this.headerCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.headerItems = response.data.results
        this.headerTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch header patients:', error)
      } finally {
        this.isHeaderLoading = false
      }
    },

    onHeaderSearch(searchTerm) {
      this.headerSearchQuery = searchTerm
      this.headerCurrentPage = 1
      this.fetchHeaderPatients()
    },

    // Footer slot example methods
    async fetchFooterPatients() {
      this.isFooterLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.footerSearchQuery || '',
          page: this.footerCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.footerItems = response.data.results
        this.footerTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch footer patients:', error)
      } finally {
        this.isFooterLoading = false
      }
    },

    onFooterSearch(searchTerm) {
      this.footerSearchQuery = searchTerm
      this.footerCurrentPage = 1
      this.fetchFooterPatients()
    },

    // Combined slot example methods
    async fetchCombinedPatients() {
      this.isCombinedLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.combinedSearchQuery || '',
          page: this.combinedCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.combinedItems = response.data.results
        this.combinedTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch combined patients:', error)
      } finally {
        this.isCombinedLoading = false
      }
    },

    onCombinedSearch(searchTerm) {
      this.combinedSearchQuery = searchTerm
      this.combinedCurrentPage = 1
      this.fetchCombinedPatients()
    },

    // Shared action methods for header/footer examples
    addNewPatient() {
      this.showMessage(['success', 'Add patient dialog would open here'])
    },

    exportPatients() {
      this.showMessage(['info', 'Export functionality would be triggered'])
    },

    showAdvancedSearch() {
      this.showMessage(['info', 'Advanced search panel would open'])
    },

    // Validation methods
    validateDefaultDropdown() {
      this.$nextTick(() => {
        const dropdownRefArray = this.$refs.validationDefaultDropdown;
        const dropdownRef = Array.isArray(dropdownRefArray) ? dropdownRefArray[0] : dropdownRefArray;
        if (dropdownRef && typeof dropdownRef.validate === 'function') {
          const isValid = dropdownRef.validate();
          this.validationDefaultResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.validationDefaultResult = null;
          }, 3000);
        } else {
          console.error('Dropdown ref not found or validate method not available');
        }
      });
    },

    validateCustomDropdown() {
      this.$nextTick(() => {
        const dropdownRefArray = this.$refs.validationCustomDropdown;
        const dropdownRef = Array.isArray(dropdownRefArray) ? dropdownRefArray[0] : dropdownRefArray;
        if (dropdownRef && typeof dropdownRef.validate === 'function') {
          const isValid = dropdownRef.validate();
          this.validationCustomResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.validationCustomResult = null;
          }, 3000);
        } else {
          console.error('Dropdown ref not found or validate method not available');
        }
      });
    },

    validateMultipleDropdown() {
      this.$nextTick(() => {
        const dropdownRefArray = this.$refs.validationMultipleDropdown;
        const dropdownRef = Array.isArray(dropdownRefArray) ? dropdownRefArray[0] : dropdownRefArray;
        if (dropdownRef && typeof dropdownRef.validate === 'function') {
          const isValid = dropdownRef.validate();
          this.validationMultipleResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.validationMultipleResult = null;
          }, 3000);
        } else {
          console.error('Dropdown ref not found or validate method not available');
        }
      });
    },

    validateChipsDropdown() {
      this.$nextTick(() => {
        const dropdownRefArray = this.$refs.validationChipsDropdown;
        const dropdownRef = Array.isArray(dropdownRefArray) ? dropdownRefArray[0] : dropdownRefArray;
        if (dropdownRef && typeof dropdownRef.validate === 'function') {
          const isValid = dropdownRef.validate();
          this.validationChipsResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.validationChipsResult = null;
          }, 3000);
        } else {
          console.error('Dropdown ref not found or validate method not available');
        }
      });
    },

    // Validation fetch methods
    async fetchValidationItems() {
      this.isValidationLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.validationSearchQuery || '',
          page: this.validationCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.validationItems = response.data.results
        this.validationTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch validation patients:', error)
      } finally {
        this.isValidationLoading = false
      }
    },

    onValidationSearch(searchTerm) {
      this.validationSearchQuery = searchTerm
      this.validationCurrentPage = 1
      this.fetchValidationItems()
    },

    async fetchValidationCustomItems() {
      this.isValidationCustomLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.validationCustomSearchQuery || '',
          page: this.validationCustomCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.validationCustomItems = response.data.results
        this.validationCustomTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch validation custom patients:', error)
      } finally {
        this.isValidationCustomLoading = false
      }
    },

    onValidationCustomSearch(searchTerm) {
      this.validationCustomSearchQuery = searchTerm
      this.validationCustomCurrentPage = 1
      this.fetchValidationCustomItems()
    },

    async fetchValidationMultipleItems() {
      this.isValidationMultipleLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.validationMultipleSearchQuery || '',
          page: this.validationMultipleCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.validationMultipleItems = response.data.results
        this.validationMultipleTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch validation multiple patients:', error)
      } finally {
        this.isValidationMultipleLoading = false
      }
    },

    onValidationMultipleSearch(searchTerm) {
      this.validationMultipleSearchQuery = searchTerm
      this.validationMultipleCurrentPage = 1
      this.fetchValidationMultipleItems()
    },

    async fetchValidationChipsItems() {
      this.isValidationChipsLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.validationChipsSearchQuery || '',
          page: this.validationChipsCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.validationChipsItems = response.data.results
        this.validationChipsTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch validation chips patients:', error)
      } finally {
        this.isValidationChipsLoading = false
      }
    },

    onValidationChipsSearch(searchTerm) {
      this.validationChipsSearchQuery = searchTerm
      this.validationChipsCurrentPage = 1
      this.fetchValidationChipsItems()
    },

    // v-form validation methods
    validateBasicForm() {
      const isValid = this.$refs.basicForm.validate()
      this.basicFormValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.basicFormValidationResult = null
      }, 1500)
    },
    resetBasicForm() {
      this.$refs.basicForm.resetValidation()
      this.basicFormValue = null
      this.basicFormValidationResult = null
    },
    validateCustomRulesForm() {
      const isValid = this.$refs.customRulesForm.validate()
      this.customRulesValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.customRulesValidationResult = null
      }, 1500)
    },
    resetCustomRulesForm() {
      this.$refs.customRulesForm.resetValidation()
      this.customRulesValue = null
      this.customRulesValidationResult = null
    },
    validateMultipleRulesForm() {
      const isValid = this.$refs.multipleRulesForm.validate()
      this.multipleRulesValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.multipleRulesValidationResult = null
      }, 1500)
    },
    resetMultipleRulesForm() {
      this.$refs.multipleRulesForm.resetValidation()
      this.multipleRulesValue = []
      this.multipleRulesValidationResult = null
    },
    validateChipsMultipleRulesForm() {
      const isValid = this.$refs.chipsMultipleRulesForm.validate()
      this.chipsMultipleRulesValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.chipsMultipleRulesValidationResult = null
      }, 1500)
    },
    resetChipsMultipleRulesForm() {
      this.$refs.chipsMultipleRulesForm.resetValidation()
      this.chipsMultipleRulesValue = []
      this.chipsMultipleRulesValidationResult = null
    },

    // v-form validation API fetch methods
    async fetchBasicFormPatients() {
      this.isBasicFormLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.basicFormSearchQuery || '',
          page: this.basicFormCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.basicFormItems = response.data.results
        this.basicFormTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch basic form patients:', error)
      } finally {
        this.isBasicFormLoading = false
      }
    },

    onBasicFormSearch(searchTerm) {
      this.basicFormSearchQuery = searchTerm
      this.basicFormCurrentPage = 1
      this.fetchBasicFormPatients()
    },

    async fetchCustomRulesPatients() {
      this.isCustomRulesLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.customRulesSearchQuery || '',
          page: this.customRulesCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.customRulesItems = response.data.results
        this.customRulesTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch custom rules patients:', error)
      } finally {
        this.isCustomRulesLoading = false
      }
    },

    onCustomRulesSearch(searchTerm) {
      this.customRulesSearchQuery = searchTerm
      this.customRulesCurrentPage = 1
      this.fetchCustomRulesPatients()
    },

    async fetchMultipleRulesPatients() {
      this.isMultipleRulesLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.multipleRulesSearchQuery || '',
          page: this.multipleRulesCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.multipleRulesItems = response.data.results
        this.multipleRulesTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch multiple rules patients:', error)
      } finally {
        this.isMultipleRulesLoading = false
      }
    },

    onMultipleRulesSearch(searchTerm) {
      this.multipleRulesSearchQuery = searchTerm
      this.multipleRulesCurrentPage = 1
      this.fetchMultipleRulesPatients()
    },

    async fetchChipsRulesPatients() {
      this.isChipsRulesLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: this.chipsRulesSearchQuery || '',
          page: this.chipsRulesCurrentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.chipsRulesItems = response.data.results
        this.chipsRulesTotalCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch chips rules patients:', error)
      } finally {
        this.isChipsRulesLoading = false
      }
    },

    onChipsRulesSearch(searchTerm) {
      this.chipsRulesSearchQuery = searchTerm
      this.chipsRulesCurrentPage = 1
      this.fetchChipsRulesPatients()
    },

    // Optional Validation Methods
    async fetchDepartments() {
      this.isOptionalDepartmentLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: '',
          page: this.optionalDepartmentPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.optionalDepartmentItems = response.data.results
        this.optionalDepartmentCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch department patients:', error)
      } finally {
        this.isOptionalDepartmentLoading = false
      }
    },

    onDepartmentSearch() {
      // Trigger API call with search
      this.fetchDepartments()
    },

    async fetchUsers() {
      this.isOptionalUserLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: '',
          page: this.optionalUserPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.optionalUserItems = response.data.results
        this.optionalUserCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch user patients:', error)
      } finally {
        this.isOptionalUserLoading = false
      }
    },

    onUserSearch() {
      // Trigger API call with search
      this.fetchUsers()
    },

    async fetchProjects() {
      this.isOptionalProjectLoading = true
      try {
        const params = {
          fields: 'id,name,phone,ic,profile_image',
          search: '',
          page: this.optionalProjectPage,
          ordering: '-created'
        }
        const response = await this.$axios.get('/clinics/codeblue/patients', {params})
        this.optionalProjectItems = response.data.results.map(item => ({
          ...item,
          title: item.name
        }))
        this.optionalProjectCount = response.data.count
      } catch (error) {
        console.error('Failed to fetch project patients:', error)
      } finally {
        this.isOptionalProjectLoading = false
      }
    },

    onProjectSearch() {
      // Trigger API call with search
      this.fetchProjects()
    },

    validateOptionalDepartmentForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalDepartmentForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalDepartmentValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalDepartmentValidationResult = null;
          }, 1500);
        }
      });
    },

    resetOptionalDepartmentForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalDepartmentForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalDepartmentValidationResult = null;
          this.optionalDepartmentValue = null;
        }
      });
    },

    validateOptionalUserForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalUserForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalUserValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalUserValidationResult = null;
          }, 1500);
        }
      });
    },

    resetOptionalUserForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalUserForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalUserValidationResult = null;
          this.optionalUserValue = null;
        }
      });
    },

    validateOptionalProjectForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalProjectForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalProjectValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalProjectValidationResult = null;
          }, 1500);
        }
      });
    },

    resetOptionalProjectForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalProjectForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalProjectValidationResult = null;
          this.optionalProjectValue = null;
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}

.variation-preview {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
}

.code-block {
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
  white-space: pre;
  background-color: #f5f5f5;
  font-size: 12px;
  line-height: 1.4;
}

.props-table {
  .v-data-table td {
    padding: 8px 16px;
    border-bottom: 1px solid #e0e0e0;
  }

  .single-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
  }
}

h3, h4 {
  color: #1976d2;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
}

.rotating {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>